import { Node, mergeAttributes } from '@tiptap/core'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { ySyncPluginKey } from '@tiptap/y-tiptap'

/**
 * Een citatie in de tekst (R-14, R-50, eisenboek 12.9).
 *
 * Een inline knooppunt met een `citatieId` (stabiel, voor de citatie-engine) en de
 * bronnen die hij citeert, als `SourceId`'s in de bronnenopslag van deze sectie
 * (`packages/renderer/src/document/sectiedocument.ts`, `SOURCES`). Naar CitatenPOC's
 * `CitationNode` (bijlage C.2): de knoop draagt **nooit het zichtbare label**. Dat
 * label hangt af van elke andere citatie in het voorstel (disambiguatie, "2026a/2026b")
 * en is daarom afgeleid, door de citatie-engine (`packages/renderer/src/citatie/`),
 * en niet bewaard.
 *
 * **Het label komt van buiten de knoop, per editor.** De citatie-engine rekent per laag over
 * de secties in de projectie (`citation/useSectionCitations.ts`), en `Richtext` zet de
 * uitkomst in de opslag van deze extensie (`editor.storage.referentie`). De weergave van de
 * knoop leest daaruit en tekent opnieuw als de uitkomst verandert; in het document komt
 * niets, net als bij CitatenPOC's `CitationNodeView` met zijn `ResultsContext`. Zonder
 * uitkomst voor deze citatie (nog niet berekend, of een bron die er niet is) staat er
 * `(…)`, zoals in CitatenPOC: beter geen label dan een label dat kan tegenspreken.
 *
 * **Geen React in deze knoop.** Deze extensie staat in de gedeelde lijst (`schema.ts`) die
 * de pod ook leest om een schema te bouwen; een weergave in gewone DOM houdt `@tiptap/react`
 * daar buiten. `getSchema` raakt de weergave en de opslag niet, dus het schema van de knoop
 * (naam en attributen) blijft precies wat het was (valkuilen 2 en 3).
 *
 * `renderHTML` blijft de vorm voor HTML buiten een editor (kopiëren, `getHTML`): daar is geen
 * uitkomst, dus de plaatshouder.
 *
 * Twee velden en niet één: `bronnen` is een lijst, want CitatenPOC staat meerdere
 * bronnen in één citatie toe (`(Jansen, 2025; Van Dijk, 2026)`), en dat is met deze
 * vorm al mogelijk zonder latere migratie.
 */
export interface ReferenceAttributes {
  readonly citatieId: string
  readonly bronnen: readonly string[]
}

/** De plaatshouder zolang er voor een citatie geen uitkomst is, naar CitatenPOC (`'(…)'`). */
const REFERENCE_PLACEHOLDER = '(…)'

/**
 * De labels van de citaties in deze editor, per `citatieId`, met wie ernaar kijkt.
 *
 * Een gewone verzameling luisteraars en geen React-state: de weergave van een knoop leeft buiten
 * de React-boom van `Richtext`, en moet bij een nieuwe uitkomst opnieuw tekenen zonder dat het
 * document verandert.
 */
export interface CitationLabels {
  get(citationId: string): string | undefined
  set(labels: ReadonlyMap<string, string>): void
  subscribe(listener: () => void): () => void
}

function createCitationLabels(): CitationLabels {
  let current: ReadonlyMap<string, string> = new Map()
  const listeners = new Set<() => void>()
  return {
    get: (citationId) => current.get(citationId),
    set(labels) {
      if (labels === current) return
      current = labels
      for (const listener of listeners) listener()
    },
    subscribe(listener) {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
  }
}

/** Hoe de volledige bronvermelding werd geopend: dat bepaalt wat haar weer sluit. */
export type PopoverTrigger = 'muis' | 'selectie' | 'focus'

/** Welke citatie nu haar volledige bronvermelding toont, en bij welk element. */
export interface PopoverState {
  readonly citationId: string
  readonly anchor: HTMLElement
  readonly via: PopoverTrigger
}

/**
 * De ene popover per editor met de volledige bronvermelding, naar CitatenPOC's `CitationNodeView`.
 *
 * De weergave van de knoop opent en sluit hem; `Richtext` tekent hem. Anders dan in CitatenPOC niet
 * alleen met de muis (R-139, R-158): ook als de citatie met een pijltoets is gekozen, en in een
 * alleen-lezen veld als ze focus heeft. Escape sluit hem, zoals het tooltippatroon van WAI-ARIA vraagt.
 */
export interface CitationPopover {
  current(): PopoverState | null
  open(state: PopoverState): void
  /** Sluit, maar alleen als hij open is voor dit element en (als gegeven) op deze manier. */
  close(anchor?: HTMLElement, via?: PopoverTrigger): void
  subscribe(listener: () => void): () => void
}

function createCitationPopover(): CitationPopover {
  let state: PopoverState | null = null
  const listeners = new Set<() => void>()
  const notify = (): void => {
    for (const listener of listeners) listener()
  }
  return {
    current: () => state,
    open(next) {
      state = next
      notify()
    },
    close(anchor, via) {
      if (state === null) return
      if (anchor !== undefined && state.anchor !== anchor) return
      if (via !== undefined && state.via !== via) return
      state = null
      notify()
    },
    subscribe(listener) {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
  }
}

export interface ReferenceStorage {
  readonly labels: CitationLabels
  readonly popover: CitationPopover
  /** Het id van het element van de popover, voor `aria-describedby`; `Richtext` zet het. */
  popoverId: string
  /**
   * Waar een nieuw citatie-id vandaan komt als een kopie er een nodig heeft. `Richtext` zet hier
   * de `newCitationId` van de afnemer neer (een id maken is niet aan een control).
   */
  newCitationId: () => string
}

declare module '@tiptap/core' {
  interface Storage {
    referentie: ReferenceStorage
  }
}

export const Reference = Node.create<Record<string, never>, ReferenceStorage>({
  name: 'referentie',
  group: 'inline',
  inline: true,
  atom: true,

  addStorage() {
    return {
      labels: createCitationLabels(),
      popover: createCitationPopover(),
      popoverId: '',
      newCitationId: () => `cit-${crypto.randomUUID()}`,
    }
  },

  /**
   * Elke citatie houdt een eigen id, naar CitatenPOC's `uniqueCitationIdPlugin`.
   *
   * Kopiëren en plakken kloont de attributen, en twee knopen met hetzelfde id delen dan één label en
   * één vermelding: de engine houdt de eerste. Hier krijgt de latere een nieuw id, ook als de eerste
   * in een ander veld of een herhaalgroeprij van de laag staat (dan kennen de labels haar). Alleen bij een
   * eigen wijziging en niet bij een die van de samenwerklaag komt, zodat niet elke medeschrijver
   * tegelijk herschrijft: wie de dubbel maakte, heelt hem, en dat komt via Yjs bij de rest. Buiten
   * de historie, en idempotent: zijn de ids uniek, dan doet de volgende ronde niets.
   */
  addProseMirrorPlugins() {
    const storage = this.storage
    return [
      new Plugin({
        key: new PluginKey('uniekeCitatieId'),
        appendTransaction(transactions, before, state) {
          const local = transactions.some(
            (tr) => tr.docChanged && (tr.getMeta(ySyncPluginKey) as { isChangeOrigin?: boolean } | undefined)?.isChangeOrigin !== true,
          )
          if (!local) return null
          // Ook een id dat al in een ander veld of een rij van de laag staat (de labels van de laag
          // kennen het) en dat hier nieuw binnenkomt: een citatie die van daar is geplakt.
          const had = new Set<string>()
          before.doc.descendants((node) => {
            if (node.type.name === 'referentie') had.add(String(node.attrs['citatieId']))
          })
          const seen = new Set<string>()
          const duplicates: number[] = []
          state.doc.descendants((node, pos) => {
            if (node.type.name !== 'referentie') return
            const id = node.attrs['citatieId'] as string | null
            if (id === null || id === '') return
            const elsewhere = !had.has(id) && storage.labels.get(id) !== undefined
            if (seen.has(id) || elsewhere) duplicates.push(pos)
            else seen.add(id)
          })
          if (duplicates.length === 0) return null
          const tr = state.tr
          for (const pos of duplicates) tr.setNodeAttribute(pos, 'citatieId', storage.newCitationId())
          return tr.setMeta('addToHistory', false)
        },
      }),
    ]
  },

  addNodeView() {
    return ({ node: initial, editor }) => {
      let node = initial
      const dom = document.createElement('span')
      dom.className = 'ef-referentie'
      dom.contentEditable = 'false'
      const { labels, popover } = editor.storage.referentie
      const citationId = (): string => String(node.attrs['citatieId'] ?? '')
      const paint = (): void => {
        dom.setAttribute('data-referentie', citationId())
        dom.textContent = labels.get(citationId()) ?? REFERENCE_PLACEHOLDER
        // In een alleen-lezen veld is er geen cursor om de citatie mee te kiezen: dan is ze zelf
        // bereikbaar met Tab (R-139, R-158).
        if (editor.isEditable) dom.removeAttribute('tabindex')
        else dom.tabIndex = 0
      }
      const describe = (): void => {
        const state = popover.current()
        if (state?.anchor === dom && editor.storage.referentie.popoverId !== '') {
          dom.setAttribute('aria-describedby', editor.storage.referentie.popoverId)
        } else dom.removeAttribute('aria-describedby')
      }
      const open = (via: PopoverTrigger) => (): void => popover.open({ citationId: citationId(), anchor: dom, via })
      const onEnter = open('muis')
      const onFocus = open('focus')
      const onLeave = (): void => popover.close(dom, 'muis')
      const onBlur = (): void => popover.close(dom, 'focus')
      dom.addEventListener('mouseenter', onEnter)
      dom.addEventListener('mouseleave', onLeave)
      dom.addEventListener('focus', onFocus)
      dom.addEventListener('blur', onBlur)
      paint()
      const stopLabels = labels.subscribe(paint)
      const stopPopover = popover.subscribe(describe)
      const stopEditable = (): void => paint()
      editor.on('update', stopEditable)
      return {
        dom,
        selectNode() {
          dom.classList.add('ProseMirror-selectednode')
          popover.open({ citationId: citationId(), anchor: dom, via: 'selectie' })
        },
        deselectNode() {
          dom.classList.remove('ProseMirror-selectednode')
          popover.close(dom, 'selectie')
        },
        update(next) {
          if (next.type !== node.type) return false
          node = next
          paint()
          return true
        },
        // Het label is van ons en niet van de invuller: een wijziging erin is geen bewerking. Een
        // selectie wel, zoals bij de standaardweergave van ProseMirror: anders raakt de editor
        // de cursor kwijt na een klik op het label.
        ignoreMutation: (mutation) => mutation.type !== 'selection',
        destroy() {
          stopLabels()
          stopPopover()
          editor.off('update', stopEditable)
          dom.removeEventListener('mouseenter', onEnter)
          dom.removeEventListener('mouseleave', onLeave)
          dom.removeEventListener('focus', onFocus)
          dom.removeEventListener('blur', onBlur)
          popover.close(dom)
        },
      }
    }
  },

  addAttributes() {
    return {
      // Naar en uit HTML voor het klembord: zonder deze twee kwam een gekopieerde citatie terug met
      // `bronnen` als tekst in plaats van een lijst, en dan telde ze nergens meer mee.
      citatieId: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('data-referentie'),
        renderHTML: (attributes: Record<string, unknown>) => ({ 'data-referentie': attributes['citatieId'] }),
      },
      bronnen: {
        default: [],
        parseHTML: (element: HTMLElement) => sourcesFromHtml(element.getAttribute('data-bronnen')),
        renderHTML: (attributes: Record<string, unknown>) => ({ 'data-bronnen': JSON.stringify(attributes['bronnen'] ?? []) }),
      },
    }
  },

  parseHTML() {
    return [{ tag: 'span[data-referentie]' }]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, { class: 'ef-referentie' }),
      REFERENCE_PLACEHOLDER,
    ]
  },
})

/** De bron-id's uit `data-bronnen`, of geen bij iets wat geen lijst van teksten is. */
function sourcesFromHtml(raw: string | null): string[] {
  if (raw === null) return []
  try {
    const parsed: unknown = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : []
  } catch {
    return []
  }
}
